import { useContext, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Context } from '../../../../context/AppContext';
import useCategorySuggest from '../../common/hooks/useCategorySuggest';
import usePopularSearches from '../../common/hooks/usePopularSearches';
import useProfileSuggest from '../../common/hooks/useProfileSuggest';
import useRecentSearches from '../../common/hooks/useRecentSearches';
import useSearchAreaSuggest from '../../common/hooks/useSearchAreaSuggest';
import { isNotEmptyArray } from '../../common/utils/helpers';
import { KEYWORD_INPUT_ID, SEARCH_AREA_INPUT_ID } from '../../constants';
import useModal from '../useModal';

const useSearch = ({ initialSearch }) => {
  const categorySuggest = useCategorySuggest();
  const popularSearches = usePopularSearches();
  const profileSuggest = useProfileSuggest();
  const recentSearches = useRecentSearches();
  const searchAreaSuggest = useSearchAreaSuggest();
  const modal = useModal();

  const focusedInput = useState();
  const [selectedSearchArea, setSelectedSearchArea] = useState();
  const [selectedKeyword, setSelectedKeyword] = useState();

  const useMyLocationButtonRef = useRef(null);

  const { setValue } = useFormContext();

  const {
    userLocation: { setLocation },
  } = useContext(Context);

  /**
   * Whenever the selected search area is chosen:
   * - Fetch the popular searches for the search area
   * - Fetch profile suggestions if a keyword has been provided
   * - Fetch other suggested search areas based on the selected search area name (only if selected earch area is not
   *   already within the suggestions array)
   * - Save the search area name as the search area input field's value
   * - Save the search area to cookies
   */
  useEffect(() => {
    if (selectedSearchArea) {
      popularSearches.fetchPopularSearches({ searchArea: selectedSearchArea });
      if (selectedKeyword) {
        profileSuggest.fetchProfileSuggestions({
          query: selectedKeyword,
          searchArea: selectedSearchArea,
          debounceFetch: false,
        });
      }
      const selectedSearchAreaNotInSugestions =
        !selectedSearchArea ||
        !isNotEmptyArray(searchAreaSuggest.searchAreaSuggestions) ||
        searchAreaSuggest.searchAreaSuggestions.findIndex(
          suggestion =>
            selectedSearchArea.suggestLabel === suggestion.suggestLabel
        ) === -1;
      if (selectedSearchAreaNotInSugestions) {
        searchAreaSuggest.fetchSearchAreaSuggestions({
          query: selectedSearchArea.name,
          debounceFetch: false,
        });
      }
      setValue(SEARCH_AREA_INPUT_ID, selectedSearchArea.name);
      setLocation({
        description: selectedSearchArea.suggestLabel,
        path: selectedSearchArea.path,
      });
    } else {
      setValue(SEARCH_AREA_INPUT_ID, '');
      searchAreaSuggest.setSearchAreaSuggestions();
      popularSearches.setPopularSearches();
      profileSuggest.setProfileSuggestions();
    }
  }, [selectedSearchArea]);

  /**
   * Whenever the selected keyword is changed:
   * - Fetch profile suggestions
   * - Fetch category suggestions for the keyword
   * - Save the keyword as the keyword input field's value
   */
  useEffect(() => {
    if (selectedKeyword) {
      setValue(KEYWORD_INPUT_ID, selectedKeyword);
      categorySuggest.fetchCategorySuggestions({
        query: selectedKeyword,
        debounceFetch: false,
      });
      profileSuggest.fetchProfileSuggestions({
        query: selectedKeyword,
        debounceFetch: false,
        ...(selectedSearchArea ? { searchArea: selectedSearchArea } : {}),
      });
    } else {
      setValue(KEYWORD_INPUT_ID, '');
      categorySuggest.setCategorySuggestions();
      profileSuggest.setProfileSuggestions();
    }
  }, [selectedKeyword]);

  return {
    categorySuggest,
    focusedInput,
    initialSearch,
    modal,
    popularSearches,
    profileSuggest,
    recentSearches,
    searchAreaSuggest,
    selectedKeyword: [selectedKeyword, setSelectedKeyword],
    selectedSearchArea: [selectedSearchArea, setSelectedSearchArea],
    useMyLocationButtonRef,
  };
};

export default useSearch;
